import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type Invoice = Schemas["StaffInvoice"];
/** One invoice as its page loads it: with its lines by order (P8-72). */
export type InvoiceDetail = Schemas["StaffInvoiceDetail"];
export type InvoiceStatus = Invoice["status"];
export type InvoiceSource = Invoice["source"];
export type InvoiceLineInput = Schemas["InvoiceLineInput"];
export type PaymentTerm = Schemas["PaymentTerm"];
export type TermKind = PaymentTerm["kind"];
export type Billee = { customer_id: string } | { business_id: string };

export const INVOICE_STATUS: Record<InvoiceStatus, [label: string, tone: Tone]> = {
  draft: ["Draft", "neutral"],
  issued: ["Issued", "info"],
  sent: ["Sent", "info"],
  partially_paid: ["Part paid", "warning"],
  paid: ["Paid", "success"],
  void: ["Void", "neutral"],
};

export const INVOICE_SOURCE: Record<InvoiceSource, string> = {
  manual: "Manual",
  order: "From an order",
  delivery_batch: "Period batch",
};

export const TERM_KIND: Record<TermKind, string> = {
  prepaid: "Paid before dispatch",
  on_delivery: "Paid on delivery",
  net_days: "Net days after the invoice",
  end_of_month: "Days after the month ends",
};

/** Issued, sent or part paid: still owed. The API's `standing=open`. */
export const OPEN: InvoiceStatus[] = ["issued", "sent", "partially_paid"];

/** Open and past its due date: shown as Overdue, in red. The API's `standing=overdue`. */
export const isOverdue = (i: Pick<Invoice, "status" | "due_at">, now = Date.now()) =>
  OPEN.includes(i.status) && i.due_at !== null && Date.parse(i.due_at) < now;

/** The badge an invoice shows: its status, or Overdue. */
export function statusBadge(i: Pick<Invoice, "status" | "due_at">, now = Date.now()): [string, Tone] {
  return isOverdue(i, now) ? ["Overdue", "danger"] : INVOICE_STATUS[i.status];
}

/** What a page of the invoice offers: a draft is edited, deleted or issued; an issued one is sent or voided. */
export function invoiceActionsFor(status: InvoiceStatus) {
  return {
    edit: status === "draft",
    remove: status === "draft",
    issue: status === "draft",
    send: OPEN.includes(status) || status === "paid",
    // a paid invoice is refunded instead (INVOICE_PAID); a draft is deleted (INVOICE_NOT_ISSUED)
    void: OPEN.includes(status),
    // money received outside the app, while something is owed (P8-71)
    record: OPEN.includes(status),
  };
}

export const invoiceQuery = (id: string) =>
  queryOptions({
    queryKey: ["invoice", id],
    queryFn: () => ok(api.GET("/v1/admin/billing/invoices/{id}", { params: { path: { id } } })),
  });

export const invoicePaymentsQuery = (id: string) =>
  queryOptions({
    queryKey: ["payments", "invoice", id],
    queryFn: async () =>
      (await ok(api.GET("/v1/admin/payments", { params: { query: { invoice_id: id, limit: 50 } } }))).data,
  });

/** The invoice's eTIMS sale (and credit note, once voided), if the company fiscalises. */
export const taxLinksQuery = (id: string) =>
  queryOptions({
    queryKey: ["tax-links", id],
    queryFn: async () => {
      const [sale, credit] = await Promise.all(
        ["invoice", "invoice_credit_note"].map(
          async (entity_type) =>
            (await ok(api.GET("/v1/admin/tax/links", { params: { query: { entity_type, entity_id: id } } }))).data,
        ),
      );
      return { sale: sale[0] ?? null, creditNote: credit[0] ?? null };
    },
  });

export const ageingQuery = queryOptions({
  queryKey: ["ageing"],
  queryFn: async () => {
    const ageing = await ok(api.GET("/v1/admin/billing/ageing", { params: { query: { format: "json" } } }));
    // the same route answers CSV for format=csv; JSON was asked for
    if (typeof ageing === "string") throw new Error("Ageing came back as CSV");
    return ageing;
  },
});

/** The PDF, through the console's own origin (the session cookie goes with it). */
/** A payer's orders that can be invoiced (P8-47); with orderIds, the totals are for those only. */
export const candidatesQuery = (
  billee: Billee | null,
  period: { start: string; end: string } | null,
  orderIds?: string[],
) =>
  queryOptions({
    queryKey: ["invoices", "candidates", billee, period, orderIds ?? null],
    enabled: billee !== null,
    queryFn: () =>
      ok(
        api.GET("/v1/admin/billing/invoices/candidates", {
          params: {
            query: {
              ...(billee ?? {}),
              ...(period ? { period_start: period.start, period_end: period.end } : {}),
              ...(orderIds ? { order_ids: orderIds.join(",") } : {}),
            },
          },
        }),
      ),
  });

/** Every payer with orders waiting to be invoiced, the longest waiting first. */
export const toInvoiceQuery = (filter: { q?: string; period?: { start: string; end: string } | null }) =>
  queryOptions({
    queryKey: ["invoices", "to-invoice", filter],
    queryFn: async () => {
      const list = await ok(
        api.GET("/v1/admin/billing/invoices/to-invoice", {
          params: {
            query: {
              format: "json",
              ...(filter.q ? { q: filter.q } : {}),
              ...(filter.period ? { period_start: filter.period.start, period_end: filter.period.end } : {}),
            },
          },
        }),
      );
      // the same route answers CSV for format=csv; JSON was asked for
      if (typeof list === "string") throw new Error("To invoice came back as CSV");
      return list;
    },
  });

export const pdfUrl = (id: string) => `/v1/admin/billing/invoices/${id}/pdf`;

const path = (id: string) => ({ params: { path: { id } } });

export const invoiceActions = {
  create: (billee: Billee, lines: InvoiceLineInput[], notes?: string) =>
    ok(api.POST("/v1/admin/billing/invoices", { body: { ...billee, lines, notes } })),
  update: (id: string, body: { lines?: InvoiceLineInput[]; notes?: string | null }) =>
    ok(api.PATCH("/v1/admin/billing/invoices/{id}", { ...path(id), body })),
  remove: (id: string) => ok(api.DELETE("/v1/admin/billing/invoices/{id}", path(id))),
  issue: (id: string) => ok(api.POST("/v1/admin/billing/invoices/{id}/issue", path(id))),
  void: (id: string, reason: string) =>
    ok(api.POST("/v1/admin/billing/invoices/{id}/void", { ...path(id), body: { reason } })),
  send: (id: string, to: { phone_number?: string | null; email?: string | null }) =>
    ok(api.POST("/v1/admin/billing/invoices/{id}/send", { ...path(id), body: to })),
  fromOrder: (orderId: string) =>
    ok(api.POST("/v1/admin/billing/invoices/from-order", { body: { order_id: orderId } })),
  /** A payer's invoiceable orders: all, or `orderIds`; only those delivered in the period when given. */
  batch: (billee: Billee, selection: { period?: { start: string; end: string }; orderIds?: string[] }) =>
    ok(
      api.POST("/v1/admin/billing/invoices/batch", {
        body: {
          ...billee,
          ...(selection.period ? { period_start: selection.period.start, period_end: selection.period.end } : {}),
          ...(selection.orderIds ? { order_ids: selection.orderIds } : {}),
        },
      }),
    ),
  resubmitTax: (id: string, document: "sale" | "credit_note") =>
    ok(
      api.POST("/v1/admin/tax/invoices/{invoice_id}/resubmit", {
        params: { path: { invoice_id: id } },
        body: { document },
      }),
    ),
};

export const termActions = {
  create: (body: Schemas["CreatePaymentTermBody"]) => ok(api.POST("/v1/admin/billing/payment-terms", { body })),
  update: (id: string, body: Schemas["UpdatePaymentTermBody"]) =>
    ok(api.PATCH("/v1/admin/billing/payment-terms/{id}", { ...path(id), body })),
};

/** What the company's invoices say about it (P8-70): Settings › Company › Invoice details. */
export type InvoiceSettings = Schemas["InvoiceSettings"];
export const invoiceSettingsQuery = queryOptions({
  queryKey: ["invoice-settings"],
  queryFn: () => ok(api.GET("/v1/admin/billing/invoice-settings", {})),
});
